/**
 * Animations made as data: PERFORMANCES -- a wave, a bow, a roar, a dance -- that any Ember body can
 * play when something asks it to (the game's `/perform` dev command; whatever sets an entity's
 * performance). Each is a keyframed clip like the game's own strikes and falls (clips.ts): a few key
 * poses of the whole body -- how it rises, leans and turns, where its arms and legs are -- eased
 * between and sampled at any frame count, in every facing, on every body. Content
 * (`content/game/sprites/ember/animations.json`), bound into EMBER_PERFORMANCES in place.
 *
 * Content writes angles in DEGREES; the engine's keys are radians (clips.ts EmberKey).
 */
import { bindContent } from "../content/catalog"
import { EmberClip, EmberKey, sampleKeyframes } from "./clips"
import { noteEmberContent } from "./model"
import { deg } from "./vec"

/** A key pose as content writes it. Angles in degrees; rise, shiftX and feet in pixels; t 0..1. */
export interface AnimationKey {
    t: number
    /** The pelvis raised (or, negative, sunk) from standing, in pixels. */
    rise?: number
    /** The whole body moved sideways (its left +), in pixels. */
    shiftX?: number
    /** The whole body rolled about its forward axis (toward its left +). */
    roll?: number
    /** The whole body pitched forward about the pelvis (a bow; a fall onto its face). */
    pitch?: number
    /** 0..1: how far the body is let down onto whatever of it is lowest (1 rests it on the ground). */
    ground?: number
    /** The chest bent forward from the hips (back: negative). */
    lean?: number
    /** The chest turned (toward its left +). */
    twist?: number
    /** The hips' and the head's own turns, relative to the feet (given both, `twist` is the chest's turn too). */
    hips?: number
    head?: number
    /** The chest bent sideways (toward its left +). */
    sideBend?: number
    /** The head nodded down (+) or tipped back (-). */
    headPitch?: number
    /** Each arm: [pitch (raised forward, 90 level, 180 straight up), abduct (out to the side), flex (the elbow bent), wrist?]. */
    armL: [number, number, number] | [number, number, number, number]
    armR: [number, number, number] | [number, number, number, number]
    /** Planted ankles [x out from the hip, z ahead], in pixels: the feet stay on the ground. */
    feetL?: [number, number]
    feetR?: [number, number]
    /** Or each leg's angles [pitch (forward), abduct (out), flex (the knee bent)]. */
    legL?: [number, number, number]
    legR?: [number, number, number]
}

export interface EmberAnimationData {
    about?: string
    /** Frames it is drawn in, and how long each shows. */
    frames: number
    frameMs: number
    /** Plays over and over until it is stopped (else once). */
    loop?: boolean
    /** Key poses, t from 0 (the first) to 1 (the last), in order. */
    keys: AnimationKey[]
}

export interface EmberAnimationFile {
    version: 1
    about?: string
    animations: Record<string, EmberAnimationData>
}

/** What a performance may be called: lowercase, a letter first; never one of the game's actions. */
export const PERFORMANCE_NAME = /^[a-z][a-z0-9_]{1,31}$/

const ANGLES = ["roll", "pitch", "lean", "twist", "hips", "head", "sideBend", "headPitch"] as const
const LIMBS = ["armL", "armR", "legL", "legR"] as const

/** A content key as the engine's (radians). */
export const engineKey = (key: AnimationKey): EmberKey => {
    const out: Record<string, unknown> = { ...key }
    for (const field of ANGLES) if (typeof key[field] === "number") out[field] = deg(key[field]!)
    for (const limb of LIMBS) if (key[limb]) out[limb] = key[limb]!.map(deg)
    return out as unknown as EmberKey
}
const toDegrees = (radians: number) => Math.round((radians * 180) / Math.PI * 1e4) / 1e4
/** An engine key as content writes it (degrees, rounded): the game's own clips shown as data. */
export const contentKey = (key: EmberKey): AnimationKey => {
    const out: Record<string, unknown> = {}
    for (const [field, value] of Object.entries(key)) {
        if (value === undefined) continue
        if ((ANGLES as readonly string[]).includes(field)) out[field] = toDegrees(value as number)
        else if ((LIMBS as readonly string[]).includes(field)) out[field] = (value as number[]).map(toDegrees)
        else out[field] = value
    }
    return out as unknown as AnimationKey
}

/** An animation as a clip the engine plays. */
export const clipOf = (name: string, a: EmberAnimationData): EmberClip => ({
    name,
    frames: a.frames,
    frameMs: a.frameMs,
    loop: !!a.loop,
    sample: sampleKeyframes(a.keys.map(engineKey)),
})

/** The performances, by name (no prototype: a name off the wire cannot reach one). Filled in place. */
export const EMBER_PERFORMANCES: Record<string, EmberClip> = Object.create(null)
let bound: EmberAnimationFile | undefined
let previewing: Record<string, EmberAnimationData> | undefined

const apply = () => {
    for (const k of Object.keys(EMBER_PERFORMANCES)) delete EMBER_PERFORMANCES[k]
    for (const [name, a] of Object.entries({ ...(bound?.animations ?? {}), ...(previewing ?? {}) })) EMBER_PERFORMANCES[name] = clipOf(name, a)
}

/** Fill the performances from content's animations.json (bound below), with any preview over them. */
export const registerEmberAnimations = (file: EmberAnimationFile | undefined): void => {
    bound = file
    apply()
    noteEmberContent("animations", file)
}

/** Animations a tool is previewing, over the game's: a draft plays under its name before it exists. Undefined clears them. */
export const setPreviewEmberAnimations = (animations: Record<string, EmberAnimationData> | undefined): void => {
    previewing = animations
    apply()
    noteEmberContent("animations-preview", animations)
}

export const isEmberPerformance = (name: string | undefined): boolean => !!name && Object.prototype.hasOwnProperty.call(EMBER_PERFORMANCES, name)
/** How long a performance plays once, in milliseconds. */
export const performanceDurationMs = (name: string): number | undefined => {
    const clip = EMBER_PERFORMANCES[name]
    return clip ? clip.frames * clip.frameMs : undefined
}
/** The game's animations as content writes them (what the Lab and tools read). */
export const emberAnimationsFile = (): EmberAnimationFile | undefined => bound

bindContent(catalog => (catalog.source as any).emberAnimations, registerEmberAnimations)
